<template>
    <v-dialog v-model="internalDialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="text-h5 primary white--text">
                <v-icon dark left>mdi-account-plus</v-icon>
                Add Members to {{ companyName }}
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-6 pb-4">
                <v-form ref="form" v-model="valid">
                    <!-- Team Members Selection -->
                    <div class="mb-4">
                        <label class="text-subtitle-1 font-weight-medium mb-2 d-block">
                            Select Members <span class="red--text">*</span>
                        </label>
                        <v-autocomplete
                            v-model="selectedMembers"
                            :items="availableUsers"
                            item-text="name"
                            item-value="id"
                            placeholder="Search and select members to add"
                            outlined
                            dense
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            :rules="[rules.required]"
                            clearable
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 5" small close @click:close="removeMember(item.id)" color="primary" text-color="white">
                                    <v-avatar left size="24" :color="getAvatarColor(item.id)">
                                        <span class="white--text text-caption">{{ getUserInitials(item) }}</span>
                                    </v-avatar>
                                    {{ getUserDisplayName(item) }}
                                </v-chip>
                                <span v-if="index === 5" class="grey--text text-caption ml-2">
                                    (+{{ selectedMembers.length - 5 }} others)
                                </span>
                            </template>
                            <template v-slot:item="{ item }">
                                <v-list-item-avatar :color="getAvatarColor(item.id)">
                                    <span class="white--text">{{ getUserInitials(item) }}</span>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>{{ getUserDisplayName(item) }}</v-list-item-title>
                                    <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                        <div class="text-caption grey--text mt-1">
                            Select one or more members to add to this company
                        </div>
                    </div>

                    <!-- Selected Count -->
                    <v-alert v-if="selectedMembers.length > 0" dense outlined type="info" class="mb-0">
                        <div class="d-flex align-center">
                            <v-icon left>mdi-information</v-icon>
                            <span>{{ selectedMembers.length }} member{{ selectedMembers.length > 1 ? 's' : '' }} will be added</span>
                        </div>
                    </v-alert>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="addMembers" :loading="loading" :disabled="!valid || selectedMembers.length === 0">
                    <v-icon left>mdi-account-plus</v-icon>
                    Add {{ selectedMembers.length > 0 ? selectedMembers.length : '' }} Member{{ selectedMembers.length !== 1 ? 's' : '' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'AddMemberDialog',
    inject: ['users'],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        companyId: {
            type: Number,
            required: true
        },
        companyName: {
            type: String,
            default: 'Company'
        },
        existingMemberIds: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            internalDialog: this.value,
            valid: false,
            loading: false,
            selectedMembers: [],
            rules: {
                required: (v) => (v && v.length > 0) || 'Please select at least one member'
            }
        };
    },
    computed: {
        usersList() {
            return this.users() || [];
        },
        availableUsers() {
            // Filter out users who are already members
            return this.usersList.filter(user => !this.existingMemberIds.includes(user.id));
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
            if (val) {
                this.resetForm();
            }
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.resetForm();
            }
        }
    },
    methods: {
        getUserDisplayName(user) {
            if (!user) return 'Unknown User';
            if (user.name) return user.name;
            if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
            if (user.first_name) return user.first_name;
            if (user.email) return user.email;
            return 'Unknown User';
        },
        closeDialog() {
            this.internalDialog = false;
        },
        removeMember(memberId) {
            const index = this.selectedMembers.indexOf(memberId);
            if (index > -1) {
                this.selectedMembers.splice(index, 1);
            }
        },
        resetForm() {
            this.selectedMembers = [];
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        getUserInitials(user) {
            if (!user) return '??';
            if (user.first_name && user.last_name) {
                return (user.first_name[0] + user.last_name[0]).toUpperCase();
            }
            if (user.name) {
                const names = user.name.split(' ');
                if (names.length >= 2) {
                    return (names[0][0] + names[1][0]).toUpperCase();
                }
                return user.name.substring(0, 2).toUpperCase();
            }
            return '??';
        },
        getAvatarColor(userId) {
            const colors = ['#4285F4', '#EA4335', '#FBBC04', '#34A853', '#9C27B0', '#FF6D00', '#00ACC1'];
            return colors[userId % colors.length];
        },
        async addMembers() {
            if (!this.$refs.form.validate() || this.selectedMembers.length === 0) {
                return;
            }

            this.loading = true;
            try {
                // Call API to add members to company
                const payload = {
                    access: this.companyId,
                    user_ids: this.selectedMembers
                };

                const response = await this.$axios.post('api/companies/create_multi_company_access', payload);
                
                if (response.data.response || response.data.success) {
                    const memberCount = this.selectedMembers.length;
                    this.$toast.success(`${memberCount} member${memberCount > 1 ? 's' : ''} added successfully`);
                    this.$emit('members-added', this.selectedMembers);
                    this.closeDialog();
                } else {
                    throw new Error(response.data.message || 'Failed to add members');
                }
            } catch (error) {
                console.error('Error adding members:', error);
                this.$toast.error(error.response?.data?.message || error.message || 'Failed to add members');
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.v-card-title {
    border-bottom: 1px solid #e0e0e0;
}

label {
    color: #333;
}
</style>
